import { Add, Smartphone } from '@material-ui/icons';
import { TextCell } from 'components/Cells/Cells';
import Button from 'components/CustomButtons/Button';
import CustomTable from 'components/CustomTable/CustomTable';
import { translate } from 'components/Translation';
import { BodyS } from 'components/Typography/Typography';
import React, { useState } from 'react';
import { BulkNumber } from 'views/BulkPurchaseDevices/BulkPurchase/index';
import styles from 'assets/jss/material-dashboard-pro-react/views/bulkPurchase/bulkNumbersListStyle';
import { makeStyles } from '@material-ui/core';
import AddDeviceDialog from 'views/BulkPurchaseDevices/BulkPurchase/AddDeviceDialog';
import ConditionalRender from 'components/ConditionalRender/ConditionalRender';
import { EshopProductsService } from 'services/eshop/products';
import { finalize } from 'rxjs/operators';
import CustomCircularProgress from 'components/CustomCircularProgress/CustomCircularProgress';
import { SingleCorpAccount } from 'utils/api/models/accounts/singleCorpAccount';

// @ts-ignore
const useStyles = makeStyles(styles);

const BulkNumbersList = ({
	numbers,
	deleteNumbersFromCart,
	fetchCartData,
	backFn,
}: {
	numbers: BulkNumber[];
	fetchCartData: () => void;
	deleteNumbersFromCart: (nums: BulkNumber[]) => void;
	backFn: () => void;

}) => {
	const classes = useStyles();
	const [selected, setSelected] = useState<number[]>([]);
	const [deleteCounter, setDeleteCounter] = useState<number>(0);

	const [isDeviceDialogOpen, setIsDeviceDialogOpen] = useState<boolean>(false);
	const [selectedNumber, setSelectedNumber] = useState<BulkNumber>();

	const chooseAddDeviceData = (number: BulkNumber) => {
		setIsDeviceDialogOpen(true);
		setSelectedNumber(number);
	};

	const deleteNumbers = () => {
		const numbersCartToDelete = numbers.filter((num, index) => selected.includes(index) && !!num?.orderItemId);
		setDeleteCounter(numbersCartToDelete?.length || 0);
		if (numbersCartToDelete?.length) {
			numbersCartToDelete.forEach((num) => {
				EshopProductsService.removeSingleCartItem(num?.orderItemId || '')
					.pipe(
						finalize(() => {
							setDeleteCounter((prevState) => (prevState ? prevState - 1 : 0));
						})
					)
					.subscribe(
						() => {
							if (deleteCounter <= 0) {
								deleteNumbersFromCart(numbers.filter((_, index) => selected.includes(index)));
								setSelected([]);
							}
						},
						() => {
							setDeleteCounter(0);
						}
					);
			});
		} else {
			setDeleteCounter(0);
			deleteNumbersFromCart(numbers.filter((_, index) => selected.includes(index)));
			setSelected([]);
		}
	};

	const fetchCart = () => {
		setIsDeviceDialogOpen(false);
		fetchCartData();
	};

	return (
		<>
			<CustomTable
				withCheckbox
				withHeader
				selected={selected}
				selectPanel={{
					selectedElements: selected.length,
					confirmationDialogBody: (
						<BodyS color="primary-light" disableMargin inline>
							{translate('purchase.bulk.devices.numbers.list.delete.cart.item')}
						</BodyS>
					),
					deleteItems: deleteNumbers,
				}}
				onSelect={setSelected}
				total={numbers.length}
				tableProps={{
					data: numbers || [],
					loading: false,
					columns: [
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.number'),
							accessor: 'number',
							sortable: false,
							Cell: function NumberCell({ original }: { original: BulkNumber }) {
								return <TextCell text={original.accountNumber || 'No data'} />;
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.tariff'),
							accessor: 'tariffName',
							sortable: false,
							Cell: function TariffNameCell({ original }: { original: BulkNumber }) {
								return (
									<TextCell
										text={
											original.tariffName ||
											translate('purchase.bulk.devices.numbers.list.columns.loading')
										}
									/>
								);
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.allowance'),
							accessor: 'discount',
							sortable: false,
							Cell: function DiscountCell({ original }: { original: BulkNumber }) {
								return <TextCell text={translate('price', original.discount || 0)} />;
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.device'),
							accessor: 'device',
							sortable: false,
							width: 200,
							Cell: function DeviceCell({ original }: { original: BulkNumber }) {
								return (
									<ConditionalRender
										show={!!original?.device}
										onTrue={() => (
											<div className={classes.deviceName}>
												<TextCell
													title={original?.device || ''}
													text={original?.device || ''}
												/>
											</div>
										)}
										onFalse={() => (
											<Button
												onClick={() => {
													chooseAddDeviceData(original);
												}}
												color="tertiary-light"
												disabled={!original.tariffName}
												withIcon
											>
												<>
													<Smartphone />
													{translate('purchase.bulk.devices.numbers.list.add.device')}
												</>
											</Button>
										)}
									/>
								);
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.commitment'),
							accessor: 'commitment',
							sortable: false,
							Cell: function CommitmentCell({ original }: { original: BulkNumber }) {
								return <TextCell text={original?.commitmentDate || ''} />;
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.current.price'),
							accessor: 'currentPrice',
							sortable: false,
							Cell: function CurrentPriceCell({ original }: { original: BulkNumber }) {
								return (
									<TextCell
										text={
											['0', 'null', 'undefined'].indexOf(String(original?.currentPrice)) > -1
												? ''
												: translate('price', Number(original?.currentPrice).toFixed(3) || '')
										}
										bold
									/>
								);
							},
						},
						{
							Header: translate('purchase.bulk.devices.numbers.list.columns.prediscount.price'),
							accessor: 'prediscountPrice',
							sortable: false,
							Cell: function PrediscountPriceCell({ original }: { original: BulkNumber }) {
								return (
									<ConditionalRender
										show={!!original?.prediscountPrice}
										onTrue={() => (
											<span className={classes.prediscount}>
												<TextCell
													text={translate(
														'price',
														original?.prediscountPrice
															? Number(original?.prediscountPrice).toFixed(3)
															: ''
													)}
												/>
											</span>
										)}
										onFalse={() => (
											<TextCell
												text={
													original?.prediscountPrice
														? Number(original?.prediscountPrice).toFixed(3)
														: ''
												}
											/>
										)}
									/>
								);
							},
						},
					],
					defaultPageSize: numbers.length > 10 ? 10 : numbers.length,
					pageSize: numbers.length > 10 ? 10 : numbers.length,
					pageSizeOptions: [numbers.length, 10, 20, 25, 50, 100],
				}}
			/>
			<div className={classes.footerContainer}>
				<Button onClick={backFn} color="tertiary-light" withIcon>
					<>
						<Add />
						{translate('purchase.bulk.devices.numbers.list.add.next')}
					</>
				</Button>
			</div>
			<AddDeviceDialog
				hideDialog={() => setIsDeviceDialogOpen(false)}
				isOpen={isDeviceDialogOpen}
				number={selectedNumber}
				onAddToCart={fetchCart}
			/>
			<ConditionalRender
				show={deleteCounter > 0}
				onTrue={() => (
					<div className={classes.loadingWrapper}>
						<CustomCircularProgress
							loading
							text={translate('purchase.bulk.devices.voucher.loader.title')}
							transparent
						/>
					</div>
				)}
			/>
		</>
	);
};

export default BulkNumbersList;
